import React, { useState } from 'react'
import axios from 'axios'

import {
  Link,
  useNavigate
} from 'react-router-dom'

import { useSelector } from 'react-redux'

import logo from '../../logo.png'

import {
  FiArrowRight,
  FiEye,
  FiEyeOff,
  FiHome,
  FiLock,
  FiMail,
  FiShield
} from 'react-icons/fi'


export default function Login() {

  const navigate = useNavigate()

  const url =
    useSelector(state => state.url)

  const [email, setEmail] =
    useState('')

  const [password, setPassword] =
    useState('')

  const [showPassword, setShowPassword] =
    useState(false)

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')


  const login = async (event) => {

    event.preventDefault()

    setError('')

    const cleanEmail =
      email.trim().toLowerCase()


    if (!cleanEmail) {

      setError(
        'Please enter your email address.'
      )

      return
    }


    if (!password) {

      setError(
        'Please enter your password.'
      )

      return
    }


    try {

      setLoading(true)
      const response =
        await axios.post(
          `${url}/auth/login`,
          {
            email: cleanEmail,
            password
          }
        )
      const data =
        response.data
      if (data.status_code === 200) {
        localStorage.setItem(
          'access_token',
          data.access_token
        )
        navigate(
          '/dashboard/home'
        )
        return
      }

      setError(
        data.error ||
        'Unable to sign in with those details.'
      )
    } catch (err) {
      console.error(
        'Login error:',
        err
      )
      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to sign in. Please try again.'
      )

    } finally {

      setLoading(false)
    }
  }


  return (

    <div className="
      min-h-screen
      bg-[#F7F9FC]
      font-[Satoshi]
      grid
      grid-cols-1
      lg:grid-cols-2
    ">


      {/* LEFT SIDE */}

      <section className="
        hidden
        lg:flex
        relative
        overflow-hidden
        bg-[#101828]
        text-white
        p-12
        xl:p-16
        flex-col
        justify-between
        min-h-screen
      ">


        <div className="
          absolute
          w-[500px]
          h-[500px]
          rounded-full
          bg-green-500/10
          -right-52
          -top-44
        " />


        <div className="
          absolute
          w-80
          h-80
          rounded-full
          bg-green-500/5
          -left-32
          bottom-20
        " />


        <Link
          to="/"
          className="
            relative
            flex
            items-center
            gap-3
            w-fit
          "
        >

          <div className="
            w-11
            h-11
            rounded-xl
            bg-white
            flex
            items-center
            justify-center
            overflow-hidden
          ">

            <img
              src={logo}
              alt="RentHub"
              className="
                w-full
                h-full
                object-cover
              "
            />

          </div>


          <span className="
            text-xl
            font-bold
          ">
            RentHub
          </span>

        </Link>


        <div className="
          relative
          max-w-xl
        ">

          <div className="
            inline-flex
            items-center
            gap-2
            text-green-300
            bg-green-500/10
            border
            border-green-500/20
            rounded-full
            px-3
            py-2
            text-xs
            font-bold
            mb-7
          ">

            <FiHome />

            RENT FINANCING MADE SIMPLE

          </div>


          <h1 className="
            text-4xl
            xl:text-5xl
            font-bold
            tracking-tight
            leading-tight
          ">
            Annual rent.
            <br />

            <span className="
              text-green-400
            ">
              Monthly repayments.
            </span>
          </h1>


          <p className="
            text-gray-400
            text-lg
            leading-8
            mt-6
            max-w-lg
          ">
            RentHub helps eligible tenants cover annual rent upfront and repay conveniently over time.
          </p>


          <div className="
            grid
            grid-cols-2
            gap-4
            mt-10
          ">

            <Feature
              icon={<FiShield />}
              text="Secure account"
            />

            <Feature
              icon={<FiHome />}
              text="Rent financing"
            />

          </div>

        </div>


        <p className="
          relative
          text-xs
          text-gray-500
        ">
          © 2026 RentHub. All rights reserved.
        </p>

      </section>



      {/* LOGIN SIDE */}

      <section className="
        min-h-screen
        flex
        flex-col
      ">


        {/* MOBILE HEADER */}

        <div className="
          lg:hidden
          flex
          items-center
          justify-between
          px-5
          py-5
        ">

          <Link
            to="/"
            className="
              flex
              items-center
              gap-2
            "
          >

            <img
              src={logo}
              alt="RentHub"
              className="
                w-10
                h-10
                rounded-xl
                object-cover
              "
            />

            <span className="
              font-bold
              text-gray-900
            ">
              RentHub
            </span>

          </Link>

        </div>


        <div className="
          flex-1
          flex
          items-center
          justify-center
          px-5
          sm:px-8
          py-10
        ">

          <div className="
            w-full
            max-w-[440px]
          ">


            <div className="mb-8">

              <p className="
                text-sm
                font-semibold
                text-green-600
                mb-2
              ">
                Welcome back
              </p>


              <h2 className="
                text-3xl
                lg:text-4xl
                font-bold
                tracking-tight
                text-gray-900
              ">
                Sign in to RentHub
              </h2>


              <p className="
                text-gray-500
                mt-3
                leading-6
              ">
                Access your rent financing, repayments and account details.
              </p>

            </div>



            {error && (

              <div className="
                bg-red-50
                border
                border-red-200
                rounded-xl
                px-4
                py-3
                text-sm
                text-red-600
                mb-6
              ">
                {error}
              </div>

            )}



            <form
              onSubmit={login}
              className="
                space-y-5
              "
            >


              {/* EMAIL */}

              <div>

                <label
                  htmlFor="email"
                  className="
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                    mb-2
                  "
                >
                  Email address
                </label>


                <div className="relative">

                  <FiMail className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  " />


                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(
                        event.target.value
                      )
                    }
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="
                      w-full
                      h-[54px]
                      pl-11
                      pr-4
                      bg-white
                      border
                      border-gray-300
                      rounded-xl
                      outline-none
                      text-gray-900
                      transition
                      focus:border-green-500
                      focus:ring-4
                      focus:ring-green-50
                    "
                  />

                </div>

              </div>



              {/* PASSWORD */}

              <div>

                <div className="
                  flex
                  justify-between
                  items-center
                  gap-4
                  mb-2
                ">

                  <label
                    htmlFor="password"
                    className="
                      text-sm
                      font-semibold
                      text-gray-700
                    "
                  >
                    Password
                  </label>


                  <Link
                    to="/auth/forgot_password/"
                    className="
                      text-sm
                      font-semibold
                      text-green-600
                      hover:text-green-700
                    "
                  >
                    Forgot password?
                  </Link>

                </div>


                <div className="relative">

                  <FiLock className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  " />


                  <input
                    id="password"
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    value={password}
                    onChange={(event) =>
                      setPassword(
                        event.target.value
                      )
                    }
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="
                      w-full
                      h-[54px]
                      pl-11
                      pr-12
                      bg-white
                      border
                      border-gray-300
                      rounded-xl
                      outline-none
                      text-gray-900
                      transition
                      focus:border-green-500
                      focus:ring-4
                      focus:ring-green-50
                    "
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        current => !current
                      )
                    }
                    className="
                      absolute
                      right-4
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                      hover:text-gray-700
                      transition
                    "
                    aria-label={
                      showPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >

                    {
                      showPassword
                        ? <FiEyeOff />
                        : <FiEye />
                    }

                  </button>

                </div>

              </div>



              {/* SUBMIT */}

              <button
                type="submit"
                disabled={loading}
                className="
                  w-full
                  h-[54px]
                  bg-green-600
                  hover:bg-green-700
                  active:bg-green-800
                  disabled:bg-green-400
                  disabled:cursor-not-allowed
                  text-white
                  rounded-xl
                  font-bold
                  transition
                  flex
                  items-center
                  justify-center
                  gap-2
                  mt-2
                "
              >

                {
                  loading
                    ? (
                      <>
                        <span className="
                          w-5
                          h-5
                          border-2
                          border-white/40
                          border-t-white
                          rounded-full
                          animate-spin
                        " />

                        Signing in...
                      </>
                    )
                    : (
                      <>
                        Sign in

                        <FiArrowRight />
                      </>
                    )
                }

              </button>

            </form>



            <div className="
              flex
              items-center
              gap-4
              my-8
            ">

              <div className="
                h-px
                bg-gray-200
                flex-1
              " />

              <span className="
                text-xs
                text-gray-400
                font-medium
              ">
                NEW TO RENTHUB?
              </span>

              <div className="
                h-px
                bg-gray-200
                flex-1
              " />

            </div>


            <Link
              to="/auth/signup/"
              className="
                w-full
                h-[52px]
                border
                border-gray-300
                hover:border-gray-400
                hover:bg-white
                rounded-xl
                font-semibold
                text-sm
                text-gray-700
                flex
                items-center
                justify-center
                transition
              "
            >
              Create a RentHub account
            </Link>


            <p className="
              text-xs
              text-gray-400
              text-center
              leading-5
              mt-8
            ">
              By continuing, you agree to RentHub's terms and privacy policy.
            </p>

          </div>

        </div>

      </section>

    </div>
  )
}



function Feature({
  icon,
  text
}) {

  return (

    <div className="
      flex
      items-center
      gap-3
      bg-white/5
      border
      border-white/10
      rounded-xl
      px-4
      py-4
    ">

      <div className="
        text-green-400
        text-lg
      ">
        {icon}
      </div>

      <p className="
        text-sm
        text-gray-300
        font-medium
      ">
        {text}
      </p>

    </div>
  )
}